import type { Staff, AvailabilitySlot, StaffAvailabilityRow } from '../types/staff';
import { DateTime } from "luxon";
import { getAppointmentRegistry } from "@/lib/appointment/appointmentRegistrySingleton";
import { resolveMediaUrl } from "@/lib/resolveMediaUrl";

export type ScheduledStaff = Staff & { intervals: { start: number; end: number }[] };

export class StaffRegistry {
  private staffById: Map<number, Staff>;
  private scheduleByDate: Map<string, ScheduledStaff[]>;
  // NEW: date -> array of { staffId, slots[] }
  private availabilityByDate: Map<string, StaffAvailabilityRow[]>;

  constructor() {
    this.staffById = new Map();
    this.scheduleByDate = new Map();
    this.availabilityByDate = new Map();
  }

  addOrUpdateStaff(staff: Staff) {
    const idNum = typeof staff.id === "string" ? Number(staff.id) : staff.id;
    const prev = this.staffById.get(idNum);
    if (!prev) {
      this.staffById.set(idNum, staff);
      return;
    }
    // Merge without losing previously known fields like weight
    this.staffById.set(idNum, {
      ...prev,
      ...staff,
      // If incoming weight is undefined/null/NaN, keep the previous one
      weight:
        typeof staff.weight === "number" && Number.isFinite(staff.weight)
          ? staff.weight
          : prev.weight,
    });
  }

  getStaffById(id: string | number): Staff | undefined {
    const idNum = typeof id === 'string' ? Number(id) : id;
    return this.staffById.get(idNum);
  }

  getAllStaff(): Staff[] {
    return Array.from(this.staffById.values());
  }

  /**
   * Returns a map of staffId → staff object.
   * @returns Record<number, Staff>
   */
  getStaffByIdMap(): Record<number, Staff> {
    const map: Record<number, Staff> = {};
    for (const [id, staff] of this.staffById.entries()) {
      map[id] = staff;
    }
    return map;
  }

  /**
   * Parses included arrays of staff data and merges into the staff map.
   * Deduplicates and updates staff by ID, supporting incremental merging.
   * @param includedArrays Array of included arrays from JSON responses.
   */
  buildStaffMap(includedArrays: any[][]) {
    includedArrays.forEach((included) => {
      if (!included) return;
      included
        .filter((item: any) => item.type === 'staff' || item.type === 'master')
        .forEach((item: any) => {
          const canonicalId = Number(
            item.attributes?.master_id   // normal payloads
            ?? item.attributes?.staff_id // just‑in‑case
            ?? item.id                   // legacy ystaff_id (fallback)
          );

          const idNum = canonicalId;
          // Always update/merge staff by ID (latest wins)
          const staff: Staff = {
            id: idNum,
            name: item.attributes.name || item.attributes.title || 'Unknown',
            specialization: item.attributes.specialization || item.attributes.position || '',
            avatar: resolveMediaUrl(item.attributes.image || item.attributes.avatar) || '/placeholder-user.jpg',
            // optional; only set when present
            weight: Number.isFinite(Number(item?.attributes?.weight))
              ? Number(item.attributes.weight)
              : undefined,
          };
          this.addOrUpdateStaff(staff);
        });
    });
  }

  /**
   * Convenience method: parses a single included array (from a mock or API response)
   * and merges staff entries into the registry.
   * Returns the number of staff added/updated.
   */
  parseIncluded(included: any[]): number {
    if (!Array.isArray(included)) return 0;
    const prevCount = this.staffById.size;
    this.buildStaffMap([included]);
    return this.staffById.size - prevCount;
  }

  /**
   * Builds or updates the schedule by date using the provided schedule data.
   * In addition to the usual roster mapping it *promotes* staff information
   * that lives on each `tt_day_schedule_master` resource, so the registry
   * always knows every specialist even if the API forgets to include
   * `…master.master` in the `include` list.
   *
   * @param scheduleData Array of `tt_day_schedule` documents.
   * @param included     Optional `included` array from the API response.
   */
  buildScheduleByDate(scheduleData: any[], included?: any[]): void {
    if (!Array.isArray(scheduleData)) return;

    /* ───────────────────────────────────────────────────────────────
     * 1️⃣  PROMOTE STAFF FROM `tt_day_schedule_master`
     *     (this is the small but critical fix)
     * ───────────────────────────────────────────────────────────── */
    if (Array.isArray(included)) {
      included
        .filter((res: any) => res.type === "tt_day_schedule_master")
        .forEach((master: any) => {
          const staffId = Number(
            master.attributes?.master_id ??
            master.attributes?.staff_id ??
            (master.id ? master.id.split("_")[0] : undefined)
          );
          if (!Number.isFinite(staffId) || this.staffById.has(staffId)) return;

          const staff: Staff = {
            id: staffId,
            name: master.attributes.master_name ??
              master.attributes.name ??
              master.attributes.title ??
              "Unknown",
            specialization: master.attributes.specialization ??
              master.attributes.position ??
              "",
            avatar: resolveMediaUrl(master.attributes.avatar_url ??
              master.attributes.image ??
              master.attributes.avatar) ||
              "/placeholder-user.jpg",
            weight: Number.isFinite(Number(master.attributes?.weight))
              ? Number(master.attributes.weight)
              : undefined,
          };
          this.addOrUpdateStaff(staff);
        });

      /* Still merge any dedicated `staff` / `master` resources so we
         pick up richer fields when they *are* present. */
      included
        .filter((res: any) => res.type === "staff" || res.type === "master")
        .forEach((item: any) => {
          const canonicalId = Number(
            item.attributes?.master_id ??
            item.attributes?.staff_id ??
            item.id
          );
          const staff: Staff = {
            id: canonicalId,
            name: item.attributes.name || item.attributes.title || "Unknown",
            specialization:
              item.attributes.specialization ||
              item.attributes.position ||
              "",
            avatar:
              resolveMediaUrl(item.attributes.image ||
              item.attributes.avatar) ||
              "/placeholder-user.jpg",
            weight: Number.isFinite(Number(item?.attributes?.weight))
              ? Number(item.attributes.weight)
              : undefined,
          };
          this.addOrUpdateStaff(staff);
        });
    }

    /* ───────────────────────────────────────────────────────────────
     * 2️⃣  BUILD  <date, staffId → intervals[]>  LOOK‑UP
     * ───────────────────────────────────────────────────────────── */
    const intervalsByDateAndStaff: Record<
      string,
      Record<number, { start: number; end: number }[]>
    > = {};

    if (Array.isArray(included)) {
      included
        .filter((res: any) => res.type === "tt_day_schedule_master")
        .forEach((master: any) => {
          const [rawStaffId, dateFromId] = (master.id || "").split("_");
          const staffId = Number(
            master.attributes?.master_id ?? rawStaffId
          );
          const date = master.attributes?.date ?? dateFromId;
          if (!date) return;

          const intervals: { start: number; end: number }[] = [];

          if (Array.isArray(master.attributes?.intervals)) {
            master.attributes.intervals.forEach((ivl: any) => {
              intervals.push({ start: ivl.start, end: ivl.end });
            });
          } else if (
            typeof master.attributes?.start_time === "number" &&
            typeof master.attributes?.end_time === "number"
          ) {
            intervals.push({
              start: master.attributes.start_time,
              end: master.attributes.end_time,
            });
          }

          if (!intervalsByDateAndStaff[date])
            intervalsByDateAndStaff[date] = {};
          intervalsByDateAndStaff[date][staffId] = intervals;
        });
    }

    /* ───────────────────────────────────────────────────────────────
     * 3️⃣  WALK EVERY `tt_day_schedule` DOCUMENT & BUILD ROSTERS
     * ───────────────────────────────────────────────────────────── */
    scheduleData.forEach((dayEntry: any) => {
      const dateStr: string = dayEntry.attributes.date; // YYYY‑MM‑DD

      const masterRefs =
        dayEntry.relationships?.tt_day_schedule_masters?.data ?? [];
      const specialistIds = masterRefs.map((m: any) =>
        Number((m.id || "").split("_")[0])
      );

      let staffList: ScheduledStaff[] = specialistIds.map((id: number) => {
        const staff = this.staffById.get(id);
        const intervals =
          intervalsByDateAndStaff[dateStr]?.[id] ?? [];

        if (staff) return { ...staff, intervals };

        /* (Should be rare now) */
        console.warn(
          `[StaffRegistry] buildScheduleByDate: Staff ID ${id} not found in registry for ${dateStr}. ` +
          "Available staff IDs:",
          Array.from(this.staffById.keys()),
        );

        return {
          id,
          name: "Unknown",
          specialization: "",
          avatar: "/placeholder-user.jpg",
          intervals,
        };
      });

      /* ────────────────────────────────────────────────────────────
       * 4️⃣  FALLBACK: derive virtual shifts from appointments when
       *     backend sends no roster at all for that date
       * ──────────────────────────────────────────────────────────── */
      if (staffList.length === 0) {
        const appts = getAppointmentRegistry().getAppointmentsForDate(dateStr);

        if (appts?.length) {
          const buckets = new Map<
            number,
            { staff: Staff; min: number; max: number }
          >();

          appts.forEach((appt: any) => {
            const s = (appt.staff ?? appt.master) as Staff | undefined;
            if (!s) return;

            const toSec = (t: string) => {
              const [h, m] = t.split(":").map(Number);
              return h * 3600 + m * 60;
            };

            const startSec = toSec(appt.startTime);
            const endSec = toSec(appt.endTime);

            const bucket = buckets.get(+s.id) ?? {
              staff: this.staffById.get(+s.id) ?? {
                id: s.id,
                name: s.name || "Unknown",
                specialization: s.specialization || "",
                avatar: s.avatar || "/placeholder-user.jpg",
              },
              min: startSec,
              max: endSec,
            };

            bucket.min = Math.min(bucket.min, startSec);
            bucket.max = Math.max(bucket.max, endSec);
            buckets.set(+s.id, bucket);
          });

          staffList = Array.from(buckets.values()).map(({ staff, min, max }) => ({
            ...staff,
            intervals: [{ start: min, end: max }],
          }));
        }
      }

      /* ────────────────────────────────────────────────────────────
       * 5️⃣  STORE IN `scheduleByDate`
       * ──────────────────────────────────────────────────────────── */
      this.scheduleByDate.set(dateStr, staffList);
    });
  }


  /**
   * Retrieves the staff schedule for a given date.
   * @param dateStr Date string in 'YYYY-MM-DD' format.
   * @returns Array of ScheduledStaff scheduled for the date, or empty array if none.
   */
  getScheduleByDate(dateStr: string): ScheduledStaff[] {
    return this.scheduleByDate.get(dateStr) || [];
  }

  /**
   * Parses an incoming JSON response and updates staff and schedule maps.
   * Accepts a JSON object with optional 'included' (staff) and 'data' (schedule) arrays.
   * This is idempotent and can be called multiple times with overlapping data.
   * Logs how many new staff and schedule entries were added.
   * @param json API or mock response containing staff and/or schedule data.
   * @param label Optional label for logging (e.g., request number or file name)
   */
  parseResponse(json: any, label?: string) {
    if (!json) return;
    const prevStaffCount = this.staffById.size;
    const prevScheduleCount = this.scheduleByDate.size;
    // Handle staff (included)
    if (Array.isArray(json.included)) {
      this.buildStaffMap([json.included]);
    }
    // Handle schedule (data)
    if (Array.isArray(json.data)) {
      console.log(`[StaffRegistry] parseResponse: Processing ${json.data.length} schedule entries`);
      this.buildScheduleByDate(json.data, json.included);
    }
    const newStaffCount = this.staffById.size - prevStaffCount;
    const newScheduleCount = this.scheduleByDate.size - prevScheduleCount;
    const tag = label ? `[${label}] ` : '';
    // eslint-disable-next-line no-console
    console.log(`${tag}[StaffRegistry] parseResponse: +${newStaffCount} staff, +${newScheduleCount} schedule entries`);
  }

  /**
   * Merge staff availability payload from /company/timetable/staff_availability.
   * Idempotent: upserts staff and replaces that staff's availability for given dates.
   * Expects payload like 34-response.json (masters[].availability[].slots[]).
   */
  mergeStaffAvailability(json: any) {
    if (!json || !Array.isArray(json.masters)) return;

    for (const m of json.masters) {
      const staffId = Number(m.id);
      // Ensure staff map is populated/updated (promote known fields)
      this.addOrUpdateStaff({
        id: staffId,
        name: m.name || "Unknown",
        specialization: m.specialization || "",
        avatar: resolveMediaUrl(m.avatar_url) || "/placeholder-user.jpg",
        weight: m.weight,
        information: m.information || "",
        reviews: m.reviews,
      });

      const days = Array.isArray(m.availability) ? m.availability : [];
      for (const day of days) {
        const date: string | undefined = day?.date;
        if (!date) continue;

        const slots: AvailabilitySlot[] = Array.isArray(day.slots)
          ? day.slots.map((s: any) => ({
            start: Number(s.start_sec) || 0,
            end: Number(s.end_sec) || 0,
            startStr: s.start,
            endStr: s.end,
          }))
          : [];

        // Upsert per-date collection
        const list = this.availabilityByDate.get(date) ?? [];
        const idx = list.findIndex((r) => r.staffId === staffId);
        if (idx >= 0) list[idx] = { staffId, slots };
        else list.push({ staffId, slots });
        this.availabilityByDate.set(date, list);
      }
    }
  }

  /** Get availability rows for a date (optionally filter by masters[]) */
  getAvailabilityForDate(date: string, masters?: number[]): StaffAvailabilityRow[] {
    const all = this.availabilityByDate.get(date) ?? [];
    if (!masters?.length) return all;
    const allow = new Set(masters.map(Number));
    return all.filter((row) => allow.has(row.staffId));
  }

  /** Convenience: map of date -> rows[] for multiple dates */
  getAvailabilityForDates(dates: string[], masters?: number[]): Record<string, StaffAvailabilityRow[]> {
    const out: Record<string, StaffAvailabilityRow[]> = {};
    for (const d of dates) out[d] = this.getAvailabilityForDate(d, masters);
    return out;
  }

  /** Convenience: get slots for one staff on a date */
  getAvailabilityForStaffOnDate(staffId: number, date: string): AvailabilitySlot[] {
    const rows = this.getAvailabilityForDate(date, [staffId]);
    return rows[0]?.slots ?? [];
  }

  /**
   * Returns the week schedule as an array of { date, specialistIds } objects,
   * for the 7-day period starting at the given startDate (inclusive).
   * @param startDate ISO date string (e.g., "2025-05-19")
   */
  getWeekSchedule(startDate: string): { date: string; specialistIds: number[] }[] {
    const result: { date: string; specialistIds: number[] }[] = [];
    const baseDate = DateTime.fromISO(startDate);
    for (let i = 0; i < 7; i++) {
      const date = baseDate.plus({ days: i });
      const dateStr = date.toISODate()!;
      const staffList = this.getScheduleByDate(dateStr);
      const specialistIds = staffList.map(s => typeof s.id === 'string' ? Number(s.id) : s.id);
      result.push({ date: dateStr, specialistIds });
    }
    return result;
  }

  /**
   * Returns an array of { date, hasSchedule } for the given staffId and weekDates.
   * @param staffId The staff member's ID
   * @param weekDates Array of ISO date strings (e.g., ["2025-05-19", ...])
   */
  getWeekScheduleForStaff(staffId: number, weekDates: string[]): { date: string; hasSchedule: boolean }[] {
    return weekDates.map(date => {
      const staffList = this.getScheduleByDate(date);
      const hasSchedule = staffList.some(s => (typeof s.id === 'string' ? Number(s.id) : s.id) === staffId);
      return { date, hasSchedule };
    });
  }

  /**
   * Returns the normalized staff schedule for a given date as an array of Specialist-like objects, including intervals.
   * @param date Date string in 'YYYY-MM-DD' format.
   * @returns Array of { id, name, role, avatar, intervals }
   */
  getDailySchedule(date: string): { id: number; name: string; role: string; avatar: string; intervals: { start: number; end: number }[] }[] {
    const staffList = this.getScheduleByDate(date);

    // helper accessors
    const weightOf = (s: ScheduledStaff) => {
      // First try the latest weight from staffById (populated by fetchStaffAvailability)
      const latestStaff = this.staffById.get(s.id);
      if (latestStaff && Number.isFinite(latestStaff.weight)) {
        return latestStaff.weight as number;
      }
      // Fallback to the weight in the scheduled entry
      return Number.isFinite(Number(s.weight)) ? Number(s.weight) : 0;
    };
    const earliestStart = (s: ScheduledStaff) => {
      if (!s.intervals || s.intervals.length === 0) return Number.POSITIVE_INFINITY;
      let min = Number.POSITIVE_INFINITY;
      for (const it of s.intervals) {
        const st = Number(it.start);
        if (Number.isFinite(st) && st < min) min = st;
      }
      return min;
    };

    // weight DESC → earlier time ASC → name ASC (ru)
    staffList.sort((a, b) => {
      const wa = weightOf(a), wb = weightOf(b);
      if (wa !== wb) return wb - wa;
      const ea = earliestStart(a), eb = earliestStart(b);
      if (ea !== eb) return ea - eb;
      return (a.name || "").localeCompare(b.name || "", "ru");
    });

    return staffList.map((staff) => ({
      id: typeof staff.id === 'string' ? Number(staff.id) : staff.id,
      name: staff.name,
      role: staff.specialization || "staff",
      avatar: staff.avatar,
      intervals: staff.intervals || [],
    }));
  }
}
